import React, { useState } from 'react';
import { Mail, CheckCircle } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { sendLeadToResend } from '../utils/resend';

const getUTMParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || null,
    utm_medium: urlParams.get('utm_medium') || null,
    utm_campaign: urlParams.get('utm_campaign') || null,
  };
};

const NewsletterSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const utmParams = getUTMParams();

      // Save to Supabase
      const { error } = await supabase
        .from('newsletter_signups')
        .insert({
          email,
          name: 'Newsletter Subscriber',
          service_interest: 'Newsletter Subscription',
          page_url: window.location.href,
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          user_agent: navigator.userAgent,
        });

      if (error) {
        if (error.code === '23505') {
          alert('This email is already subscribed!');
        } else {
          throw error;
        }
        return;
      }

      // Send email notification via Resend
      await sendLeadToResend({
        name: 'Newsletter Subscriber',
        email,
        serviceInterest: 'Newsletter Subscription',
        siteName: 'RadonMap Canada',
        pageName: 'Newsletter Signup',
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        utmParams
      });

      setIsSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Newsletter signup error:', error);
      alert('There was an error signing up. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-green-900 mb-2">Thank You!</h3>
        <p className="text-green-700">You've been subscribed to our radon safety updates.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center mb-4">
        <Mail className="h-6 w-6 text-red-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Stay Informed</h3>
      </div>
      
      <p className="text-gray-600 mb-4">
        Get the latest radon safety information and testing updates for Canada.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            required
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition duration-200 disabled:opacity-50"
        >
          {isLoading ? 'Subscribing...' : 'Subscribe to Updates'}
        </button>
        
        <p className="text-xs text-gray-500">
          By subscribing, you agree to receive radon safety updates. You can unsubscribe anytime. 
          We follow CASL guidelines and respect your privacy.
        </p>
      </form>
    </div>
  );
};

export default NewsletterSignup;